import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';
import isValidUrl from '@atlassian/jira-common-util-is-url/src/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { LazySmartLink } from '@atlassian/jira-issue-field-smart-link/src/ui/smart-link/async.tsx';
import type { singleLineText_issueFieldSingleLineTextReadviewFull_SingleLineTextReadView$key as SingleLineTextFragment } from '@atlassian/jira-relay/src/__generated__/singleLineText_issueFieldSingleLineTextReadviewFull_SingleLineTextReadView.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import messages from '@atlassian/jira-issue-empty-field-text/src/ui/messages.tsx';
import { useBespokeEmptyFieldTextGA } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-ga/index.tsx';
import type { SingleLineTextReadViewProps } from './types.tsx';

/**
 * The SingleLineTextReadView will show a read only view of SingleLineText field.
 * @param props [SingleLineTextReadViewProps](./types.tsx)
 */
export const SingleLineTextReadView = ({
	fragmentRef,
	renderFieldContentWrapper: FieldContentWrapper,
	renderCustomReadView,
}: SingleLineTextReadViewProps): React.JSX.Element => {
	const { text = null, fieldId } = useFragment<SingleLineTextFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment singleLineText_issueFieldSingleLineTextReadviewFull_SingleLineTextReadView on JiraSingleLineTextField {
				text
				fieldId
			}
		`,
		fragmentRef,
	);

	const isBespokeEmptyFieldTextGA = useBespokeEmptyFieldTextGA();

	const getReadView = useCallback(() => {
		if (renderCustomReadView) {
			return renderCustomReadView(text);
		}

		if (!text) {
			return (
				<EmptyFieldText
					{...(isBespokeEmptyFieldTextGA && {
						placeholderText: messages.bespokenTextPlaceholderEmptyFieldText,
					})}
				/>
			);
		}

		const testId = `issue.views.field.single-line-text.read-view.${fieldId}`;

		if (
			isValidUrl(text, true) &&
			expValEquals('jsw_perf_format-links-in-custom-fields', 'issue-view', true)
		) {
			if (fg('enable_redaction_support_in_jira_issue_view')) {
				return (
					<SpotlightTarget name={`redaction-marker-${fieldId}`}>
						<ReadViewContainer data-testid={testId}>
							<LazySmartLink appearance="inline" url={text} />
						</ReadViewContainer>
					</SpotlightTarget>
				);
			}
			return (
				<ReadViewContainer data-testid={testId}>
					<LazySmartLink appearance="inline" url={text} />
				</ReadViewContainer>
			);
		}

		if (fg('enable_redaction_support_in_jira_issue_view')) {
			return (
				<SpotlightTarget name={`redaction-marker-${fieldId}`}>
					<ReadViewContainer data-testid={testId}>{text}</ReadViewContainer>
				</SpotlightTarget>
			);
		}

		return <ReadViewContainer data-testid={testId}>{text}</ReadViewContainer>;
	}, [renderCustomReadView, text, fieldId, isBespokeEmptyFieldTextGA]);

	if (FieldContentWrapper) {
		return <FieldContentWrapper>{getReadView()}</FieldContentWrapper>;
	}
	return getReadView();
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReadViewContainer = styled.div({
	display: 'flex',
	flex: '1 1 auto',

	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	wordBreak: 'break-word',
	position: 'relative',
	width: '100%',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .smart-link-title-wrapper': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: token('space.300'),
	},
});
